import { useEffect, useState, type FormEvent } from "react";
import { Link, Navigate, useLocation, useNavigate, useSearchParams } from "react-router";
import { isSealedCategory, MARKET_CONDITIONS, MARKET_LANGUAGES } from "../../shared/market";
import { CONDITION_LABEL, LANGUAGE_LABEL } from "../../shared/variants";
import { PROVINCES, WANT_MAX_QUANTITY, WANT_NOTE_MAX, wantTextProblem, type Province, type WantedProfile } from "../../shared/wanted";
import { useAuth } from "../auth/AuthProvider";
import { ErrorMessage, Loading } from "../components/StatusMessage";
import { useI18n } from "../i18n/I18nProvider";
import type { MessageKey } from "../i18n/messages";
import { ApiError, imageUrl } from "../lib/api";
import { useAsync } from "../lib/useAsync";
import { marketApi, type CatalogCard, type Want } from "../market/api";
import { CardPicker } from "../market/CardPicker";
import { useArs } from "../market/components";
import { WantedPostCard } from "./components";

/** /se-busca/publicar(?carta=slug) — put a card on "Se busca" (or edit one already there). */
export function WantedForm() {
  const { t } = useI18n();
  const auth = useAuth();
  const location = useLocation();
  const [params] = useSearchParams();
  const slug = params.get("carta");
  const signedIn = Boolean(auth.user);
  const profile = useAsync((signal) => (signedIn ? marketApi.wantedProfile(signal) : Promise.resolve(null)), [signedIn]);
  const mine = useAsync((signal) => (signedIn ? marketApi.wants(signal) : Promise.resolve(null)), [signedIn]);
  const preset = useAsync((signal) => (slug ? marketApi.wantedCard(slug, signal).then((r) => r.product) : Promise.resolve(null)), [slug]);
  const [picked, setPicked] = useState<CatalogCard | null>(null);

  useEffect(() => {
    document.title = `${t("wanted.formTitle")} | pokefans.fun`;
  }, [t]);

  if (!auth.loading && !auth.user) return <Navigate to={`/ingresar?next=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  if (profile.error || mine.error) return <ErrorMessage onRetry={() => (profile.reload(), mine.reload())} />;
  if (!profile.data || !mine.data || (slug && preset.loading)) return <Loading />;
  const card: CatalogCard | null = picked ?? (preset.data ? { id: preset.data.id, name: preset.data.name, setName: preset.data.setName, cardNumber: preset.data.cardNumber, rarity: preset.data.rarity, imageKey: preset.data.imageKey, category: preset.data.category } : null);
  const existing = card ? (mine.data.find((w) => w.productId === card.id) ?? null) : null;

  return (
    <div className="container section wanted-form-page">
      <p className="breadcrumbs">
        <Link to="/se-busca">{t("wanted.back")}</Link>
      </p>
      <h1>{existing?.onBoardUntil ? t("wanted.editTitle") : t("wanted.formTitle")}</h1>
      <p className="muted">{t("wanted.formIntro")}</p>
      {!card ? (
        <CardPicker label={t("wanted.pick")} onPick={setPicked} filters />
      ) : (
        <WantForm key={card.id} card={card} existing={existing} profile={profile.data} onProfile={profile.reload} onChange={() => setPicked(null)} canChange={!slug || Boolean(picked)} />
      )}
    </div>
  );
}

function WantForm({ card, existing, profile, onProfile, onChange, canChange }: { card: CatalogCard; existing: Want | null; profile: WantedProfile; onProfile: () => void; onChange: () => void; canChange: boolean }) {
  const { t, locale } = useI18n();
  const ars = useArs();
  const navigate = useNavigate();
  const sealed = isSealedCategory(card.category);
  const market = useAsync((signal) => marketApi.productSummary(card.id, signal), [card.id]);
  const [conditionMin, setConditionMin] = useState(existing?.conditionMin ?? "");
  const [language, setLanguage] = useState(existing?.language ?? "");
  const [quantity, setQuantity] = useState(existing?.quantity ?? 1);
  const [maxPrice, setMaxPrice] = useState(existing?.maxPriceArs ? String(existing.maxPriceArs) : "");
  const [showPrice, setShowPrice] = useState(existing?.showPrice ?? false);
  const [trade, setTrade] = useState(existing?.trade ?? false);
  const [note, setNote] = useState(existing?.note ?? "");
  // The profile's province, shown so they can change it (never saved without them seeing it).
  const [province, setProvince] = useState<string>(existing ? (existing.province ?? "") : (profile.province ?? ""));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const problem = wantTextProblem(note);
  const max = maxPrice ? Number(maxPrice) : null;

  const submit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (problem) return;
    setBusy(true);
    setError(null);
    try {
      const list = await marketApi.saveWant(card.id, {
        maxPriceArs: max,
        conditionMin: sealed ? null : conditionMin || null,
        language: language || null,
        quantity,
        trade,
        showPrice: showPrice && max !== null,
        note: note.trim(),
        province: province || null,
        public: true,
      });
      const saved = list.find((w) => w.productId === card.id);
      navigate(saved ? `/se-busca/${encodeURIComponent(saved.slug)}` : "/se-busca", { state: { fresh: true } });
    } catch (err) {
      const code = err instanceof ApiError ? err.code : "";
      const fields = err instanceof ApiError ? ((err.details as { fields?: string[] } | undefined)?.fields ?? []) : [];
      setError(
        fields.includes("note_link")
          ? t("wanted.noteLink")
          : fields.includes("note_contact")
            ? t("wanted.noteContact")
            : ["too_many_public", "too_many", "blocked", "category_not_allowed", "fulfilled"].includes(code)
              ? t(`wanted.err.${code}` as MessageKey)
              : t("error.generic"),
      );
      setBusy(false);
    }
  };

  return (
    <form className="wanted-form stack" onSubmit={submit}>
      <div className="picked-card">
        <img src={imageUrl(card.imageKey)} alt="" width={60} height={84} />
        <div>
          <strong>{card.name}</strong>
          <div className="muted small">{[card.setName, card.cardNumber ? `#${card.cardNumber}` : null].filter(Boolean).join(" · ")}</div>
          {market.data && market.data.count > 0 && (
            <div className="small">
              {t("wanted.marketHas", { count: market.data.count, price: ars(market.data.minPriceArs ?? 0) })} ·{" "}
              <Link to={`/mercado?producto=${card.id}&orden=price_asc`}>{t("wanted.seeOffers")}</Link>
            </div>
          )}
          {canChange && (
            <button type="button" className="btn btn-link btn-sm" onClick={onChange}>
              {t("wanted.change")}
            </button>
          )}
        </div>
      </div>

      <div className="field-row">
        {!sealed && (
          <div className="field">
            <label htmlFor="wf-cond">{t("wanted.condition")}</label>
            <select id="wf-cond" value={conditionMin} onChange={(e) => setConditionMin(e.target.value)}>
              <option value="">{t("wanted.anyCondition")}</option>
              {MARKET_CONDITIONS.map((c) => (
                <option key={c} value={c}>
                  {t("wanted.minCondition", { condition: CONDITION_LABEL[c][locale] })}
                </option>
              ))}
            </select>
          </div>
        )}
        <div className="field">
          <label htmlFor="wf-lang">{t("wanted.language")}</label>
          <select id="wf-lang" value={language} onChange={(e) => setLanguage(e.target.value)}>
            <option value="">{t("wanted.anyLanguage")}</option>
            {MARKET_LANGUAGES.map((l) => (
              <option key={l} value={l}>
                {LANGUAGE_LABEL[l]?.[locale] ?? l}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor="wf-qty">{t("wanted.quantity")}</label>
          <input id="wf-qty" type="number" min={1} max={WANT_MAX_QUANTITY} inputMode="numeric" value={quantity} onChange={(e) => setQuantity(Math.min(WANT_MAX_QUANTITY, Math.max(1, Number(e.target.value) || 1)))} />
        </div>
      </div>

      <div className="field">
        <label htmlFor="wf-max">{t("wanted.maxPrice")}</label>
        <input id="wf-max" type="number" min={1} inputMode="numeric" value={maxPrice} onChange={(e) => setMaxPrice(e.target.value)} aria-describedby="wf-max-hint" />
        <span id="wf-max-hint" className="field-hint">
          {t("wanted.maxPriceHint")}
        </span>
      </div>
      {max !== null && (
        <label className="check">
          <input type="checkbox" checked={showPrice} onChange={(e) => setShowPrice(e.target.checked)} />
          <span>
            {t("wanted.showPrice")} <span className="muted small">— {t("wanted.showPriceHint")}</span>
          </span>
        </label>
      )}
      <label className="check">
        <input type="checkbox" checked={trade} onChange={(e) => setTrade(e.target.checked)} /> {t("wanted.acceptsTrade")}
      </label>

      <div className="field">
        <label htmlFor="wf-note">{t("wanted.note")}</label>
        <textarea id="wf-note" rows={2} maxLength={WANT_NOTE_MAX} value={note} onChange={(e) => setNote(e.target.value)} aria-describedby="wf-note-hint" aria-invalid={problem ? true : undefined} />
        <span id="wf-note-hint" className={problem ? "form-error" : "field-hint"}>
          {problem === "link" ? t("wanted.noteLink") : problem === "contact" ? t("wanted.noteContact") : t("wanted.noteHint")}
        </span>
      </div>

      <div className="field">
        <label htmlFor="wf-prov">{t("wanted.yourProvince")}</label>
        <select id="wf-prov" value={province} onChange={(e) => setProvince(e.target.value)}>
          <option value="">{t("wanted.noProvince")}</option>
          {(Object.keys(PROVINCES) as Province[]).map((p) => (
            <option key={p} value={p}>
              {PROVINCES[p]}
            </option>
          ))}
        </select>
      </div>

      <AliasBox profile={profile} onSaved={onProfile} />

      <section aria-labelledby="wf-preview" className="wanted-preview">
        <h2 id="wf-preview" className="small">
          {t("wanted.preview")}
        </h2>
        <WantedPostCard
          sealed={sealed}
          report={false}
          post={{
            id: 0,
            alias: profile.alias,
            handle: profile.handle,
            province: (province || null) as Province | null,
            conditionMin: sealed || !conditionMin ? null : (conditionMin as never),
            language: language || null,
            quantity,
            trade,
            maxPriceArs: showPrice ? max : null,
            note: note.trim(),
            since: new Date().toISOString(),
          }}
        />
      </section>
      <p className="muted small">🔒 {t("wanted.privacy")}</p>
      {error && (
        <p className="form-error" role="alert">
          {error}
        </p>
      )}
      <button type="submit" className="btn btn-primary" disabled={busy || Boolean(problem)}>
        {existing?.onBoardUntil ? t("wanted.saveChanges") : t("wanted.publish")}
      </button>
    </form>
  );
}

/** "Te van a ver como Martín R." — with a nickname instead, unless they have a shop (then it's their @handle). */
function AliasBox({ profile, onSaved }: { profile: WantedProfile; onSaved: () => void }) {
  const { t } = useI18n();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(profile.nickname ?? "");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  if (profile.handle)
    return (
      <p className="small">
        {t("wanted.youAppear")} <strong>{profile.alias}</strong>. <span className="muted">{t("wanted.youAppearShop")}</span>
      </p>
    );
  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await marketApi.setNickname(value.trim());
      setEditing(false);
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError && err.code === "nickname_taken" ? t("wanted.nicknameTaken") : err instanceof ApiError && err.code === "nickname_invalid" ? t("wanted.nicknameInvalid") : t("error.generic"));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="wanted-alias">
      <p className="small">
        {t("wanted.youAppear")} <strong>{profile.alias}</strong>
        {!editing && (
          <>
            {" "}
            <button type="button" className="btn btn-link btn-sm" onClick={() => setEditing(true)}>
              {t("wanted.changeNickname")}
            </button>
          </>
        )}
      </p>
      {editing && (
        <div className="field">
          <label htmlFor="wf-nick">{t("wanted.nickname")}</label>
          <div className="btn-row">
            <input id="wf-nick" value={value} maxLength={24} placeholder={profile.defaultAlias} onChange={(e) => setValue(e.target.value)} aria-describedby="wf-nick-hint" />
            <button type="button" className="btn btn-outline btn-sm" disabled={busy} onClick={() => void save()}>
              {t("wanted.saveNickname")}
            </button>
          </div>
          <span id="wf-nick-hint" className={error ? "form-error" : "field-hint"}>
            {error ?? t("wanted.nicknameHint")}
          </span>
        </div>
      )}
    </div>
  );
}
